import { Box, Dialog, FormControlLabel, IconButton, Radio, RadioGroup, Typography, useMediaQuery, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import ReplicateQuizForm from './ReplicateQuizForm'
import NewQuizForClassForm from './NewQuizFromClassForm'
import { useUserStore } from '../../../../store/user.store'
import { Icons } from '../../../../utils/utilities'
import ManageNewQuestionBank from './ManageNewQuestionBank'
import { observer } from 'mobx-react'
import { useRoomStore } from '../../../../store/room.store'
import CreateQuickQuiz from './CreateQuickQuiz'

const quizAssignTypes = [
    { label: "Replicate Quiz", value: "replicate", description: "You can assign a quiz created for another class. Use this option when the same topics are required as in the previously conducted quiz." },
    { label: "Create new", value: "create", description: "Create a new quiz by selecting topics and questions." },
    { label: "Quick Quiz", value: "quickquiz", description: "Create a new quiz by importing questions directly, without creating a question bank." },
]


const AddQuizFromClassDialog = observer( ( { fetchQuizzesInstances, setCreateMode, room, state, setState, classType } ) => {

    const [addMode, setAddMode] = useState( "replicate" )
    const [loadingSubjects, setLoadingSubjects] = useState( false )
    const [newBankId, setNewBankId] = useState( 57 )
    const [questionBankMode, setQuestionBankMode] = useState( 'select' )

    const UserStore = useUserStore()
    const RoomStore = useRoomStore()

    const { palette, border, breakpoints } = useTheme()
    const fullscreen = useMediaQuery( breakpoints.down( 'sm' ) )

    const handleClose = async ( fetchClassesDataAgain = false, quizInstance = null ) => {
        setState( null )
        if ( fetchClassesDataAgain && quizInstance ) {
            RoomStore.fetchRooms( room.selected_date, [quizInstance] )
        }
    }

    const getSubjects = useCallback( async () => {
        setLoadingSubjects( true )
        await UserStore.fetchUserSubjects()
        setLoadingSubjects( false )
    }, [UserStore] )

    useEffect( () => {
        if ( UserStore.getErrorsStatuses?.subjects === true )
            getSubjects()
    }, [UserStore, getSubjects] )

    const isHybridRoom = classType === 'hybrid-class'
        || room?.mode === 'hybrid'
        || room?.hybrid === true
        || room?.does_exists?.meeting_type === 'hybrid-class'

    const resolvedMeetingType = isHybridRoom ? 'hybrid-class' : 'class'

    return (
        <Dialog fullScreen={fullscreen} open={state} slotProps={{
            paper: { sx: { width: { xs: "100%", sm: "95%" }, maxWidth: "2000px" } }
        }} >
            <Box padding="10px 20px" marginBottom="20px" borderBottom={border[1]} display="flex" position="sticky" top="0" bgcolor={palette.background.paper} sx={{ zIndex: 1 }} alignItems="center" justifyContent="space-between">
                <Box>
                    <Typography variant="h1" fontWeight="600" component="h1" fontSize="16px">Add quiz to the class</Typography>
                    <Typography variant="subtitle2" fontSize="12px" color='textSecondary' >{room.course_short_name ? `${room.course_short_name}, ` : ""} {room.branch_name ? `${room.branch_name}, ` : ""} {room.batch_short_name !== "NA" ? <span>Batch: <em>{room.batch_short_name}</em></span> : <span>Section: <em>{room.section_short_name}</em></span>}</Typography>
                </Box>
                <IconButton size="small" onClick={() => handleClose()} >{Icons.default.CloseIcon}</IconButton>
            </Box>
            <Box padding="0 20px 10px 20px">
                <RadioGroup
                    aria-labelledby="meeting_type"
                    name="meeting_type"
                    value={addMode}
                    onChange={event => setAddMode( event.target.value )}
                    sx={{
                        margin: 0,
                        display: "grid",
                        gap: "10px",
                        gridTemplateColumns: { md: "repeat(3,1fr)", xs: "1fr" }
                    }}
                >
                    {quizAssignTypes.map( type => (
                        <FormControlLabel sx={{ margin: 0, border: border[1], borderColor: addMode === type.value && palette.secondary.main, borderRadius: "5px", padding: "5px", gap: "10px", alignItems: { md: "flex-start", xs: "center" } }} key={type.label} value={type.value} control={<Radio color="secondary" />} label={
                            <Box flexGrow={1}>
                                <Typography fontSize="14px" variant='subtitle2'>{type.label}</Typography>
                                <Typography display={{ md: "block", xs: "none" }} fontSize="10px">{type.description}</Typography>
                            </Box>
                        } />
                    ) )}
                </RadioGroup>
            </Box>
            <Box display="grid" gridTemplateColumns={{ md: questionBankMode !== 'managequestions' ? "1fr" : "1fr 1fr", xs: "1fr" }} >
                {addMode === "replicate" && <ReplicateQuizForm fetchQuizzesInstances={fetchQuizzesInstances} handleClose={handleClose} room={room} setAddMode={setAddMode} addMode={addMode} classType={resolvedMeetingType} />}
                {addMode === "quickquiz" && <CreateQuickQuiz fetchQuizzesInstances={fetchQuizzesInstances} setNewBankId={setNewBankId} questionBankMode={questionBankMode} setQuestionBankMode={setQuestionBankMode} loadingSubjects={loadingSubjects} handleClose={handleClose} subject={UserStore.getUserSubjectsDetails?.currentSubjects?.find( sub => sub.subject_id === room.subject_id )} room={room} setAddMode={setAddMode} addMode={addMode} classType={resolvedMeetingType} />}
                {addMode === "create" && <Box overflow="visible" sx={{ display: { md: "block", xs: questionBankMode === 'managequestions' ? "none" : "block" } }}>
                    <NewQuizForClassForm fetchQuizzesInstances={fetchQuizzesInstances} setNewBankId={setNewBankId} questionBankMode={questionBankMode} setQuestionBankMode={setQuestionBankMode} loadingSubjects={loadingSubjects} handleClose={handleClose} subject={UserStore.getUserSubjectsDetails?.currentSubjects?.find( sub => sub.subject_id === room.subject_id )} room={room} setAddMode={setAddMode} addMode={addMode} classType={resolvedMeetingType} />
                </Box>}
                {questionBankMode === 'managequestions' && newBankId && <Box sx={{ position: { md: "static", xs: "static" }, right: { md: "initial", xs: "0" }, height: { md: "initial", xs: "100%" }, background: { md: "none", xs: palette.form.formCardBg } }} flexGrow={1} padding="20px" paddingTop="20px">
                    <ManageNewQuestionBank setHasNewQuestionBankSaved={() => { }} setQuestionBankMode={setQuestionBankMode} bank_id={newBankId} />
                </Box>}
            </Box>
        </Dialog>
    )
} )

export default AddQuizFromClassDialog
